import { useState, useCallback, useRef } from 'react';
import { subscriptionApi } from '../../../services/subscriptionApi';
import type { XServicesService, SubscriptionPlan, ProfileSubscription, SubscriptionRequest } from '../../../types';
import {
  useSubscriptionServicesQuery,
  useProfileSubscriptionsQuery,
  useSubscribeMutation,
  useRenewSubscriptionMutation,
  subscriptionKeys,
} from '../api/subscriptionQueries';
import { useQueryClient } from '@tanstack/react-query';

export function useSubscriptions(profileId: number | undefined) {
  const queryClient = useQueryClient();
  const servicesQuery = useSubscriptionServicesQuery(Boolean(profileId));
  const subscriptionsQuery = useProfileSubscriptionsQuery(profileId);
  const subscribeMutation = useSubscribeMutation();
  const renewMutation = useRenewSubscriptionMutation();

  const [plans, setPlans] = useState<Record<number, SubscriptionPlan[]>>({});
  // Track which serviceIds have already been fetched — avoids plans dep in useCallback
  const loadedServiceIds = useRef<Set<number>>(new Set());

  const services = servicesQuery.data || [];
  const subscriptions = subscriptionsQuery.data || [];
  const loading = (Boolean(profileId) && servicesQuery.isLoading) || (Boolean(profileId) && subscriptionsQuery.isLoading);
  const error = (Boolean(profileId) && servicesQuery.error instanceof Error ? servicesQuery.error.message : null) ||
                (Boolean(profileId) && subscriptionsQuery.error instanceof Error ? subscriptionsQuery.error.message : null);

  const loadPlansForService = useCallback(async (serviceId: number) => {
    if (loadedServiceIds.current.has(serviceId)) return; // already cached
    loadedServiceIds.current.add(serviceId);
    try {
      const list = await subscriptionApi.getPlansByService(serviceId);
      setPlans(prev => ({ ...prev, [serviceId]: list }));
    } catch {
      loadedServiceIds.current.delete(serviceId); // allow retry on failure
    }
  }, []);

  const subscribe = useCallback(async (request: SubscriptionRequest): Promise<ProfileSubscription> => {
    const result = await subscribeMutation.mutateAsync(request);
    return result;
  }, [subscribeMutation]);

  const renew = useCallback(async (subscriptionId: number, planId: number): Promise<ProfileSubscription> => {
    const result = await renewMutation.mutateAsync({ subscriptionId, planId });
    return result;
  }, [renewMutation]);

  const cancel = useCallback(async (subscriptionId: number) => {
    await subscriptionApi.cancel(subscriptionId);
    queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
  }, [queryClient]);

  const resume = useCallback(async (subscriptionId: number) => {
    if (!profileId) return;
    await subscriptionApi.resume(subscriptionId, profileId);
    queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
  }, [profileId, queryClient]);

  const getActiveFor = useCallback(
    (serviceId: number) => {
      const active = subscriptions.find(s => s.serviceId === serviceId && s.subscriptionStatus === 'ACTIVE');
      if (active) return active;
      return subscriptions.find(s => s.serviceId === serviceId && s.subscriptionStatus === 'CANCELLED' && (s.daysRemaining ?? 0) > 0);
    },
    [subscriptions]
  );

  /** All subscriptions where daysRemaining is 1-30 (drives renewal banner) */
  const renewalDueList = subscriptions.filter(s => s.isRenewalDue);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
  }, [queryClient]);

  return {
    services,
    subscriptions,
    plans,
    loading,
    error,
    loadPlansForService,
    subscribe,
    renew,
    cancel,
    resume,
    getActiveFor,
    renewalDueList,
    refresh,
  };
}
